// src/components/dashboard/no-active-venue.tsx
import { useState } from 'react';
import { Mail, Store } from 'lucide-react';
import { supabase } from '@/src/lib/supabase';
import { useOnboarding } from '@/src/hooks/useOnboarding';
import VenueDetailsForm from '@/src/components/onboarding/venue-details-form';
import type { OpeningHours } from '@/src/components/onboarding/opening-hours-picker';
import TelemetryLogo from './logo';

export default function NoActiveVenue({ onDone, onSignOut }: { onDone: () => void; onSignOut: () => void }) {
  const { createVenue } = useOnboarding();
  const [mode, setMode] = useState<'choice' | 'create'>('choice');

  async function handleSubmit(data: { name: string; cuisine: string }): Promise<{ error: string | null }> {
    const { error: err, venueId } = await createVenue(data.name);
    if (err) return { error: err };
    if (venueId && data.cuisine) {
      await supabase.from('venues').update({ cuisine: data.cuisine }).eq('id', venueId);
    }
    onDone();
    return { error: null };
  }

  if (mode === 'create') {
    return (
      <VenueDetailsForm
        title="Name your venue"
        subhead="You can fill in the rest anytime from settings."
        submitLabel="Create venue"
        onSubmit={handleSubmit}
        onBack={() => setMode('choice')}
      />
    );
  }

  return (
    <div className="fixed inset-0 bg-black text-white flex flex-col items-center justify-center px-6 text-center gap-4">
      <TelemetryLogo className="w-14 h-14 mb-2" />
      <p className="text-sm text-neutral-300 max-w-xs">You don't currently have access to a venue.</p>

      <button
        onClick={() => setMode('create')}
        className="w-full max-w-xs h-12 bg-white text-black rounded-xl text-sm font-semibold flex items-center justify-center gap-2 mt-2"
      >
        <Store size={16} /> Set up your own venue
      </button>

      <div className="flex items-center gap-2 text-[11px] text-neutral-500 mt-1">
        <Mail size={12} /> Or ask your venue owner to send you an invite.
      </div>

      <button onClick={onSignOut} className="text-[11px] text-neutral-600 hover:text-neutral-400 mt-6 uppercase tracking-widest font-bold">Sign out</button>
    </div>
  );
}